import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './MarkitdownPage.css';

export default function MarkitdownPage() {
  const [files,      setFiles]      = useState([]);
  const [results,    setResults]    = useState([]);
  const [existing,   setExisting]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [dragging,   setDragging]   = useState(false);
  const [error,      setError]      = useState(null);

  const fileInputRef = useRef();

  function loadExisting() {
    axios.get('/api/markitdown/files')
      .then(r => setExisting(r.data))
      .catch(() => {});
  }

  useEffect(() => { loadExisting(); }, []);

  function addFiles(incoming) {
    const allowed = Array.from(incoming).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...allowed.filter(f => !names.has(f.name))];
    });
  }

  function removeFile(name) {
    setFiles(prev => prev.filter(f => f.name !== name));
  }

  async function handleConvert() {
    if (!files.length) return;
    setLoading(true);
    setError(null);
    setResults([]);

    const form = new FormData();
    files.forEach(f => form.append('files', f));

    try {
      const r = await axios.post('/api/markitdown/convert', form);
      setResults(r.data.results || []);
      setFiles([]);
      loadExisting();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="mk-page">
      <h1>PDF to Markdown</h1>
      <p className="mk-sub">
        Upload PDFs to convert them to Markdown with MarkItDown. Converted files are available to download for 30 days.
      </p>

      <div
        className={`mk-dropzone ${dragging ? 'mk-dropzone--active' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)}
        />
        <div className="mk-dropzone-icon">📄</div>
        <p className="mk-dropzone-label">Drop PDFs here or <span className="mk-link">browse</span></p>
        <p className="mk-dropzone-hint">PDF files only</p>
      </div>

      {files.length > 0 && (
        <ul className="mk-file-list">
          {files.map(f => (
            <li key={f.name} className="mk-file-item">
              <span className="mk-file-icon">📄</span>
              <span className="mk-file-name">{f.name}</span>
              <span className="mk-file-size">{(f.size / 1024).toFixed(1)} KB</span>
              <button className="mk-file-remove" onClick={() => removeFile(f.name)}>✕</button>
            </li>
          ))}
        </ul>
      )}

      <div className="mk-actions">
        <button
          className="mk-btn-primary"
          onClick={handleConvert}
          disabled={!files.length || loading}
        >
          {loading ? 'Converting…' : `Convert ${files.length || ''} File${files.length !== 1 ? 's' : ''}`}
        </button>
      </div>

      {error && <div className="mk-error">{error}</div>}

      {results.length > 0 && (
        <div className="mk-results">
          <h3 className="mk-section-title">Just converted</h3>
          {results.map((r, i) => (
            <div key={i} className="mk-result-card">
              {r.filename ? (
                <a href={`/api/markitdown/download/${r.filename}`} className="mk-result-name">
                  ⬇ {r.original_name}
                </a>
              ) : (
                <span className="mk-result-error">{r.original_name} — {r.error}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mk-existing">
        <h3 className="mk-section-title">Converted files</h3>
        {existing.length === 0 ? (
          <p className="mk-empty">No files yet.</p>
        ) : (
          <ul className="mk-existing-list">
            {existing.map(f => (
              <li key={f.filename} className="mk-existing-item">
                <a href={`/api/markitdown/download/${f.filename}`} className="mk-existing-name">
                  {f.original_name}
                </a>
                <span className="mk-existing-expiry">expires in {f.days_left} day{f.days_left !== 1 ? 's' : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
